import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Shield, Zap, Star, MapPin, ArrowRight, CheckCircle2, Lock, Home, Building2, Play, Car, PawPrint, TreePine, ShoppingBag, Camera, Scale, BadgeCheck, Clock } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { TRANSLATIONS, formatPrice, CURRENCIES } from "@/lib/api";

const ADDONS = [
  { id: "fridge", label: "Fridge Interior Scrub", desc: "Deep clean inside & shelves" },
  { id: "oven", label: "Oven Professional Detail", desc: "Grease removal & polish" },
  { id: "windows", label: "Interior Windows", desc: "Streak-free finish" },
  { id: "laundry", label: "Laundry & Folding", desc: "Up to 2 loads" },
  { id: "pets", label: "Pet Home Care", desc: "Hair & odor treatment" },
];

const SQFT_BRACKETS = [
  { id: "under_1500", label: "Under 1,500 sq ft", price: 175 },
  { id: "1500_3500", label: "1,500 – 3,500 sq ft", price: 275 },
  { id: "3500_5000", label: "3,500 – 5,000 sq ft", price: 375 },
  { id: "5000_plus", label: "5,000+ sq ft", price: 500, custom: true },
];

const SPACE_TYPES = [
  { id: "office", label: "Office", mult: 1.0 },
  { id: "retail", label: "Retail", mult: 1.1 },
  { id: "gym", label: "Gym", mult: 1.3 },
  { id: "clinic", label: "Clinic", mult: 1.4 },
];

const TESTIMONIALS = [
  { quote: "Cleaner arrived 8 min early — place looked showroom-new.", name: "Priya S.", city: "New York" },
  { quote: "Booked at 11pm for a 9am deep clean. It just worked.", name: "Marcus D.", city: "Austin" },
  { quote: "The live tracking map felt like ordering magic.", name: "Sofía G.", city: "Miami" },
  { quote: "Team went above and beyond. Fridge came out gleaming.", name: "Aisha K.", city: "Chicago" },
  { quote: "Payment stays protected until the job is done — exactly what I wanted.", name: "Jonas L.", city: "Seattle" },
  { quote: "Weekly recurring now — my cleaner texts before every visit.", name: "Yuki T.", city: "San Francisco" },
];

export default function Landing() {
  const { language, currency, country, user } = useApp();
  const t = TRANSLATIONS[language] || TRANSLATIONS.en;
  const nav = useNavigate();

  const [bookingType, setBookingType] = useState("home");
  const [postal, setPostal] = useState("");
  const [beds, setBeds] = useState([2]);
  const [baths, setBaths] = useState([1]);
  const [sqft, setSqft] = useState("under_1500");
  const [spaceType, setSpaceType] = useState("office");
  const [deep, setDeep] = useState(false);
  const [addons, setAddons] = useState([]);

  const price = useMemo(() => {
    let subtotal, base, breakdown = {};
    if (bookingType === "business") {
      base = SQFT_BRACKETS.find((s) => s.id === sqft)?.price || 175;
      const mult = SPACE_TYPES.find((s) => s.id === spaceType)?.mult || 1.0;
      subtotal = base * mult;
      breakdown = { base, mult };
    } else {
      base = 65;
      const bedCost = 40 * beds[0];
      const bathCost = 20 * baths[0];
      subtotal = base + bedCost + bathCost;
      breakdown = { base, bedCost, bathCost };
    }
    if (deep) subtotal = subtotal * 1.5;
    const addonFee = 35 * addons.length;
    const total = subtotal + addonFee;
    return { ...breakdown, subtotal, addonFee, total,
             platform: total * 0.20, cleaner: total * 0.80 };
  }, [bookingType, beds, baths, sqft, spaceType, deep, addons]);

  const toggleAddon = (id) => setAddons((a) => a.includes(id) ? a.filter((x) => x !== id) : [...a, id]);

  const handleBook = () => {
    const params = new URLSearchParams({
      postal, deep: deep ? "1" : "0", addons: addons.join(","),
      country, currency, booking_type: bookingType,
    });
    if (bookingType === "home") {
      params.set("beds", beds[0]); params.set("baths", baths[0]);
    } else {
      params.set("sqft", sqft); params.set("space", spaceType);
    }
    if (!user) nav(`/signup?next=/book?${params.toString()}`);
    else nav("/book?" + params.toString());
  };

  return (
    <div>
      <span id="top" />
      {/* Hero (About anchor) — dark cinematic */}
      <section id="about" className="relative overflow-hidden bg-slate-950 text-white">
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400"
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-slate-950/30" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/60 via-transparent to-slate-950" />
        </div>
        {/* Teal glow accents */}
        <div className="absolute -top-24 -left-24 w-[520px] h-[520px] rounded-full bg-teal-500/15 blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[420px] h-[420px] rounded-full bg-teal-400/10 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-16 lg:pt-20 lg:pb-24">
          {/* Eyebrow */}
          <div className="mb-4 flex items-center gap-4" data-testid="hero-eyebrow">
            <span className="h-px w-10 bg-gradient-to-r from-transparent to-teal-400/80" />
            <span className="font-mono uppercase text-[11px] sm:text-xs tracking-[0.42em] text-teal-300">
              Professional&nbsp;Cleaning&nbsp;Services
            </span>
            <span className="h-px w-10 bg-gradient-to-l from-transparent to-teal-400/80 hidden sm:block" />
          </div>

          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl xl:text-7xl tracking-tight leading-[1.05] max-w-4xl">
            <span className="text-white drop-shadow-[0_2px_20px_rgba(0,0,0,0.5)]">{t.hero_title}</span><br />
            <span className="bg-gradient-to-r from-teal-300 via-teal-400 to-teal-500 bg-clip-text text-transparent drop-shadow-[0_0_28px_rgba(20,184,166,0.35)]">{t.hero_title_2}</span>
          </h1>

          <p className="mt-5 text-base sm:text-lg text-slate-200/90 max-w-2xl leading-relaxed">{t.hero_sub}</p>

          {/* CTAs — pill buttons */}
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => nav("/services")}
              data-testid="hero-book-cleaning-btn"
              className="group inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-teal-400 text-slate-950 font-bold text-base sm:text-lg tracking-wide shadow-[0_0_32px_rgba(45,212,191,0.55)] hover:shadow-[0_0_48px_rgba(45,212,191,0.85)] hover:bg-teal-300 transition-all"
            >
              Book a Cleaning
              <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
            </button>
            {!user && (
            <button
              onClick={() => nav("/signup?role=cleaner")}
              data-testid="hero-become-pro-btn"
              className="group inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-transparent border border-teal-400/70 text-teal-100 hover:text-white font-bold text-base sm:text-lg tracking-wide shadow-[inset_0_0_28px_rgba(20,184,166,0.15),0_0_28px_rgba(20,184,166,0.25)] hover:shadow-[inset_0_0_36px_rgba(20,184,166,0.25),0_0_44px_rgba(20,184,166,0.55)] hover:bg-teal-500/10 transition-all"
            >
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-teal-300/80">
                <Sparkles className="w-3.5 h-3.5 text-teal-200" />
              </span>
              Become a CLEANBAZ Pro
            </button>
            )}
          </div>

          {/* Trust chips */}
          <div className="mt-8 flex items-center gap-x-6 gap-y-3 flex-wrap text-sm text-slate-300">
            <Badge className="bg-teal-500/10 text-teal-200 border border-teal-400/30 hover:bg-teal-500/10 font-mono text-xs" data-testid="hero-badge">
              <MapPin className="w-3 h-3 mr-1" /> Nationwide · United States
            </Badge>
            <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-teal-400" /> Payment-protected</div>
            <div className="flex items-center gap-2"><Zap className="w-4 h-4 text-teal-400" /> Instant dispatch</div>
            <div className="flex items-center gap-2"><Star className="w-4 h-4 text-teal-400" /> Vetted CLEANBAZ Pros</div>
          </div>

          {/* Testimonials strip — illustrative examples (clearly disclosed below). */}
          <div
            className="mt-8 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur-md overflow-hidden hover:border-teal-400/30 transition-colors group"
            data-testid="hero-testimonials"
            aria-label="Example customer scenarios"
          >
            <div className="testimonials-marquee flex gap-14 py-3 px-6 whitespace-nowrap [mask-image:linear-gradient(90deg,transparent,#000_10%,#000_90%,transparent)]">
              {[...TESTIMONIALS, ...TESTIMONIALS].map((t, i) => (
                <div key={i} className="inline-flex items-center gap-3 text-[13px]">
                  <span className="inline-flex gap-0.5" aria-hidden="true">
                    {Array.from({ length: 5 }).map((_, s) => (
                      <Star key={s} className="w-3 h-3 text-teal-300 fill-teal-300" />
                    ))}
                  </span>
                  <span className="text-slate-100/95">"{t.quote}"</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-teal-300/70">
                    — {t.name} · {t.city}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-2 text-center text-[10px] font-mono uppercase tracking-[0.22em] text-slate-400/80" data-testid="hero-testimonials-disclaimer">
            Example scenarios · not individual verified customer reviews
          </div>
        </div>

        {/* Bottom fade into next section */}
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-white pointer-events-none" />
      </section>

      {/* Pricing Estimator */}
      <section id="services" aria-hidden="true" className="-mt-24 pt-24" />
      <section id="pricing" className="py-20 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-8">
            <div className="text-xs font-mono uppercase tracking-widest text-teal-600 mb-2">01 — Configure</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-slate-900">{t.pricing_title}</h2>
            <p className="mt-3 text-slate-600 text-lg">{t.pricing_sub}</p>
          </div>

          {/* Booking-type toggle */}
          <div className="inline-flex bg-slate-100 rounded-2xl p-1.5 mb-8" data-testid="booking-type-toggle">
            <button
              onClick={() => setBookingType("home")}
              data-testid="btype-home"
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${bookingType === "home" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            >
              <Home className="w-4 h-4" /> Home
            </button>
            <button
              onClick={() => setBookingType("business")}
              data-testid="btype-business"
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${bookingType === "business" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            >
              <Building2 className="w-4 h-4" /> Business / Office
            </button>
          </div>

          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7">
              <Card className="border-slate-200">
                <CardContent className="p-8 space-y-8">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Country</Label>
                      <Input value={country} readOnly data-testid="pricing-country" className="mt-2 bg-slate-50 font-mono" />
                    </div>
                    <div>
                      <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Postal Code</Label>
                      <Input value={postal} onChange={(e) => setPostal(e.target.value)} placeholder="10001" data-testid="pricing-postal" className="mt-2 font-mono" />
                    </div>
                  </div>

                  {bookingType === "home" ? (
                    <>
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Bedrooms</Label>
                          <span className="font-display font-bold text-2xl text-teal-600" data-testid="pricing-beds-val">{beds[0]}</span>
                        </div>
                        <Slider value={beds} onValueChange={setBeds} min={0} max={6} step={1} data-testid="pricing-beds-slider" />
                        <div className="flex justify-between text-xs text-slate-400 mt-1 font-mono"><span>0</span><span>6</span></div>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Bathrooms</Label>
                          <span className="font-display font-bold text-2xl text-teal-600" data-testid="pricing-baths-val">{baths[0]}</span>
                        </div>
                        <Slider value={baths} onValueChange={setBaths} min={0} max={5} step={1} data-testid="pricing-baths-slider" />
                        <div className="flex justify-between text-xs text-slate-400 mt-1 font-mono"><span>0</span><span>5</span></div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <Label className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-3 block">Square Footage</Label>
                        <div className="grid grid-cols-2 gap-3">
                          {SQFT_BRACKETS.map((s) => (
                            <button
                              key={s.id}
                              type="button"
                              onClick={() => setSqft(s.id)}
                              data-testid={`pricing-sqft-${s.id}`}
                              className={`p-4 rounded-xl border-2 text-left transition-all ${sqft === s.id ? "border-teal-500 bg-teal-50" : "border-slate-200 hover:border-slate-300"}`}
                            >
                              <div className="text-sm font-semibold text-slate-900">{s.label}</div>
                              <div className="text-xs text-slate-500 font-mono mt-1">
                                Starting at ${s.price}{s.custom ? " · Custom Quote" : ""}
                              </div>
                            </button>
                          ))}
                        </div>
                        <p data-testid="commercial-pricing-note" className="mt-3 text-xs text-slate-500 italic leading-relaxed">
                          Final pricing may vary based on space type, condition, cleaning frequency, and selected services.
                        </p>
                      </div>
                      <div>
                        <Label className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-3 block">Commercial Space Type</Label>
                        <Select value={spaceType} onValueChange={setSpaceType}>
                          <SelectTrigger data-testid="pricing-space-type" className="mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-white border border-slate-200 shadow-xl">
                            {SPACE_TYPES.map((s) => (
                              <SelectItem key={s.id} value={s.id} data-testid={`space-${s.id}`}>
                                {s.label} <span className="text-xs text-slate-400 font-mono ml-2">×{s.mult}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}

                  <div className="flex items-center justify-between p-4 rounded-xl bg-teal-50 border border-teal-100">
                    <div>
                      <div className="font-semibold text-slate-900">Deep Clean Upgrade</div>
                      <div className="text-xs text-slate-500">Applies 1.5× multiplier on subtotal</div>
                    </div>
                    <Switch checked={deep} onCheckedChange={setDeep} data-testid="pricing-deep-switch" />
                  </div>

                  <div>
                    <Label className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-3 block">Premium Add-Ons (+$35 each)</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {ADDONS.map((a) => (
                        <label key={a.id} htmlFor={`addon-${a.id}`} className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${addons.includes(a.id) ? "border-teal-500 bg-teal-50" : "border-slate-200 hover:border-slate-300"}`}>
                          <Checkbox id={`addon-${a.id}`} checked={addons.includes(a.id)} onCheckedChange={() => toggleAddon(a.id)} data-testid={`pricing-addon-${a.id}`} />
                          <div>
                            <div className="text-sm font-semibold text-slate-900">{a.label}</div>
                            <div className="text-xs text-slate-500">{a.desc}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="lg:col-span-5">
              <div className="sticky top-24">
                <Card className="border-slate-900 bg-slate-900 text-white overflow-hidden">
                  <CardContent className="p-8">
                    <div className="text-xs font-mono uppercase tracking-widest text-teal-400 mb-2">02 — Estimate · {bookingType === "business" ? "Commercial" : "Residential"}</div>
                    <div className="text-sm text-slate-400">Total (localized in {currency})</div>
                    <div className="font-display font-extrabold text-5xl lg:text-6xl mt-2" data-testid="pricing-total">{formatPrice(price.total, currency)}</div>
                    <div className="text-xs font-mono text-slate-500 mt-1">≈ ${price.total.toFixed(2)} USD</div>

                    <div className="mt-6 space-y-2 text-sm">
                      {bookingType === "home" ? (
                        <>
                          <div className="flex justify-between"><span className="text-slate-400">Base service</span><span className="font-mono">${price.base.toFixed(2)}</span></div>
                          <div className="flex justify-between"><span className="text-slate-400">Bedrooms × $40</span><span className="font-mono">${price.bedCost.toFixed(2)}</span></div>
                          <div className="flex justify-between"><span className="text-slate-400">Bathrooms × $20</span><span className="font-mono">${price.bathCost.toFixed(2)}</span></div>
                        </>
                      ) : (
                        <>
                          <div className="flex justify-between"><span className="text-slate-400">Sq ft base ({SQFT_BRACKETS.find(s => s.id === sqft)?.label})</span><span className="font-mono">${price.base.toFixed(2)}</span></div>
                          <div className="flex justify-between"><span className="text-slate-400">{SPACE_TYPES.find(s => s.id === spaceType)?.label} multiplier</span><span className="font-mono">×{price.mult.toFixed(1)}</span></div>
                        </>
                      )}
                      {deep && <div className="flex justify-between text-teal-400"><span>Deep clean multiplier</span><span className="font-mono">1.5×</span></div>}
                      {addons.length > 0 && <div className="flex justify-between"><span className="text-slate-400">Add-ons × $35</span><span className="font-mono">${price.addonFee.toFixed(2)}</span></div>}
                    </div>

                    <div className="border-t border-slate-700 mt-6 pt-4 text-xs text-slate-400 space-y-1 font-mono">
                      <div className="flex justify-between"><span>Cleaner payout (80%)</span><span>${price.cleaner.toFixed(2)}</span></div>
                      <div className="flex justify-between"><span>Platform fee (20%)</span><span>${price.platform.toFixed(2)}</span></div>
                    </div>

                    <Button onClick={handleBook} data-testid="pricing-book-btn" size="lg" className="w-full mt-6 bg-teal-500 hover:bg-teal-600 text-slate-900 font-bold h-12 gap-2">
                      {t.book_now} <ArrowRight className="w-4 h-4" />
                    </Button>
                    <div className="mt-4 flex items-start gap-2 text-xs text-slate-400">
                      <Lock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-teal-400" />
                      <span>Credit card authorized instantly. Funds released to cleaner only after 100% service completion.</span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs font-mono uppercase tracking-widest text-teal-600 mb-2">How CLEANBAZ Works</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900 max-w-2xl">Book, match, and track — in that order.</h2>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {[
              { n: "01", t: "Book & pay", d: "Configure your cleaning and pay securely. Funds held by CLEANBAZ until service is completed.", i: "https://images.unsplash.com/photo-1502005097973-6a7082348e28?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjbGVhbiUyMGtpdGNoZW4lMjBpbnRlcmlvciUyMGx1eHVyeXxlbnwwfHx8fDE3ODkxMzMxMTl8MA&ixlib=rb-4.1.0&q=85" },
              { n: "02", t: "Instant match", d: "Vetted CLEANBAZ Pros in your postal code get an alert. First to accept gets the job.", i: "https://images.unsplash.com/photo-1732395805034-e0bf859665e5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxjbGVhbmVyJTIwcG9ydHJhaXQlMjBoYXBweSUyMHdvcmtlciUyMHNtaWxpbmd8ZW58MHx8fHwxNzg5MTMzMTE5fDA&ixlib=rb-4.1.0&q=85" },
              { n: "03", t: "Track & release", d: "Watch progress live. CLEANBAZ releases the Pro's share (80%) once the booking is marked complete.", i: "https://images.unsplash.com/photo-1628745277862-bc0b2d68c50c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjbGVhbiUyMGtpdGNoZW4lMjBpbnRlcmlvciUyMGx1eHVyeXxlbnwwfHx8fDE3ODkxMzMxMTl8MA&ixlib=rb-4.1.0&q=85" },
            ].map((s) => (
              <Card key={s.n} className="border-slate-200 overflow-hidden group hover:-translate-y-1 hover:shadow-xl transition-all">
                <div className="h-40 overflow-hidden">
                  <img src={s.i} alt={s.t} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                </div>
                <CardContent className="p-6">
                  <div className="text-xs font-mono text-teal-600">{s.n}</div>
                  <div className="font-display font-bold text-xl text-slate-900 mt-1">{s.t}</div>
                  <div className="text-sm text-slate-600 mt-2 leading-relaxed">{s.d}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CLEANBAZ Services — six existing category cards.
          Home Cleaning is fully bookable today → prominent "Book Now" CTA.
          Other categories are surfaced with a "Booking Coming Soon" pill,
          in line with the current booking flow rollout. */}
      <section id="services-catalog" className="py-20 bg-white border-t border-slate-100" data-testid="home-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-teal-600 mb-2">CLEANBAZ Services</div>
              <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900 max-w-2xl">Every clean, from your kitchen to your car.</h2>
              <p className="mt-3 text-slate-600 max-w-2xl">Six professional service lines, all delivered by vetted, ID-verified CLEANBAZ Pros — all live and bookable today.</p>
            </div>
            <Button
              onClick={() => nav("/services")}
              variant="outline"
              data-testid="home-services-viewall"
              className="border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              View full catalog <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { id: "home",        icon: Home,        title: "Home Cleaning",       tag: "Residential",             bookable: true,  desc: "Standard, deep, move-in / move-out and short-stay turnovers." },
              { id: "commercial",  icon: Building2,   title: "Commercial Cleaning", tag: "Business",                bookable: true, desc: "Offices, retail, gyms, clinics, and post-construction." },
              { id: "car",         icon: Car,         title: "Car Cleaning",        tag: "Mobile · At-your-location", bookable: true, desc: "Interior, exterior, and full detail — cleaner comes to you." },
              { id: "pet",         icon: PawPrint,    title: "Pet Care",            tag: "In-home & drop-in",       bookable: true, desc: "Dog walking, sitting, drop-in visits, and overnight care." },
              { id: "outdoor",     icon: TreePine,    title: "Outdoor Cleaning",    tag: "Exterior services",       bookable: true, desc: "Yards, patios, pressure washing, and gutter clearing." },
              { id: "convenience", icon: ShoppingBag, title: "Convenience",         tag: "Errands & extras",        bookable: true, desc: "Grocery drop-off, laundry pickup, and quick-turn errands." },
            ].map((c) => {
              const Icon = c.icon;
              return (
                <Card
                  key={c.id}
                  data-testid={`home-service-card-${c.id}`}
                  className={`group relative overflow-hidden border-slate-200 hover:-translate-y-1 hover:shadow-2xl transition-all ${c.bookable ? "ring-1 ring-teal-500/25" : ""}`}
                >
                  {c.bookable && (
                    <span className="absolute top-3 right-3 z-10 text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full bg-teal-500 text-slate-950 shadow-md">
                      Bookable now
                    </span>
                  )}
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-2xl bg-teal-50 border border-teal-100 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-teal-600" />
                      </div>
                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">{c.tag}</div>
                        <div className="font-display font-bold text-lg text-slate-900">{c.title}</div>
                      </div>
                    </div>
                    <p className="mt-4 text-sm text-slate-600 leading-relaxed min-h-[42px]">{c.desc}</p>
                    <div className="mt-5">
                      {c.bookable ? (
                        <Button
                          onClick={() => {
                            // Landing uses `commercial` as the display id but
                            // the backend booking pipeline uses `business` for
                            // the same service line. Map here so the /book
                            // page opens the right flow.
                            const bt = c.id === "commercial" ? "business" : c.id;
                            const params = new URLSearchParams({ booking_type: bt, country, currency });
                            if (!user) nav(`/signup?next=/book?${params.toString()}`);
                            else nav(`/book?${params.toString()}`);
                          }}
                          data-testid={`home-service-book-${c.id}`}
                          className="w-full bg-teal-500 hover:bg-teal-600 text-slate-900 font-bold gap-2"
                        >
                          Book Now <ArrowRight className="w-4 h-4" />
                        </Button>
                      ) : (
                        <div
                          data-testid={`home-service-soon-${c.id}`}
                          className="w-full text-center px-3 py-2 rounded-md bg-slate-100 text-slate-500 text-xs font-mono uppercase tracking-widest border border-slate-200"
                        >
                          Booking coming soon
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Protection & Trust — professional trust section. Every claim
          maps to an existing platform feature; nothing unsupported. */}
      <section id="protection" className="py-20 bg-slate-950 text-white relative overflow-hidden" data-testid="home-protection">
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, #14b8a6 0, transparent 55%), radial-gradient(circle at 80% 80%, #0d9488 0, transparent 45%)" }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-xs font-mono uppercase tracking-widest text-teal-400 mb-2">Professional Protection</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl">Every clean, protected end-to-end.</h2>
            <p className="mt-3 text-slate-300">The same trust systems large marketplaces are known for — built into every CLEANBAZ booking, big or small.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
            {[
              { icon: BadgeCheck, t: "Vetted CLEANBAZ Pros",  d: "Government ID + driver's license verification, auto-insurance on file for transport jobs, and a signed CLEANBAZ Cleaner Agreement before any booking." },
              { icon: Lock,       t: "Payment-Protected Pay", d: "Card authorized at booking, funds held by CLEANBAZ, released to the Pro (80%) only after the booking is marked complete. CLEANBAZ is not a bank or escrow service." },
              { icon: Camera,     t: "Before/After Photos",   d: "Every job carries private Before/After photo documentation visible only to you and CLEANBAZ admins." },
              { icon: Scale,      t: "Fair Dispute Review",   d: "Complaints reviewed against evidence + policy — CLEANBAZ never auto-decides fault or promises reimbursement." },
            ].map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.t} className="p-6 rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-md hover:border-teal-400/40 hover:bg-white/[0.06] transition-all">
                  <div className="w-11 h-11 rounded-xl bg-teal-500/15 border border-teal-400/30 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-teal-300" />
                  </div>
                  <div className="mt-4 font-display font-bold text-lg">{f.t}</div>
                  <p className="mt-2 text-sm text-slate-300/95 leading-relaxed">{f.d}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-14 flex flex-wrap gap-3 items-center justify-between p-6 rounded-2xl border border-teal-400/25 bg-teal-500/5">
            <div>
              <div className="font-display font-bold text-xl">Ready to book — or ready to earn?</div>
              <p className="text-sm text-slate-300 mt-1">All six CLEANBAZ services are live and booking today. Cleaners can apply anytime — CLEANBAZ Admin reviews every application.</p>
            </div>
            <div className="flex gap-3 flex-wrap">
              <Button
                onClick={() => nav("/services")}
                data-testid="protection-book-btn"
                className="bg-teal-400 hover:bg-teal-300 text-slate-950 font-bold h-11 px-5 gap-2"
              >
                Book a Cleaning <ArrowRight className="w-4 h-4" />
              </Button>
              {!user && (
              <Button
                onClick={() => nav("/signup?role=cleaner")}
                data-testid="protection-become-pro-btn"
                variant="outline"
                className="bg-transparent border-teal-400/60 text-teal-100 hover:bg-teal-500/10 h-11 px-5 font-bold"
              >
                Become a CLEANBAZ Pro
              </Button>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
